import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { useApiData } from 'lib/api';
import {
  CalendarEventDateString,
  CalendarEventTilePicture
} from 'components/calendar';
import {
  Button,
  EmptyIndicator,
  LoadingIndicator,
  ModalButtonWithForm
} from 'components/common';
import { Select } from 'components/common/form';
import { useHomePageSettingsContext } from '.';

import type { ModuleInSettings } from './HomePageSettingsModules';
import type { GetCalendarEventsMinimalRes } from 'shared-types/responses';

type Props = {
  module: ModuleInSettings;
  url: string;
} & (
  | { includeActions?: false; updateModule?: never }
  | { includeActions: true; updateModule: (module: ModuleInSettings) => void }
);

const HomePageSettingsTilesCalendarEvents = ({
  includeActions = false,
  module,
  updateModule,
  url
}: Props) => {
  const { messages, theme } = useReduxState();

  const { eventOptions } = useHomePageSettingsContext();

  const { data, loading } = useApiData<GetCalendarEventsMinimalRes>({
    url,
    onlyIf: Boolean(url)
  });

  const copyEvents = () => [...module.events];

  const handleAddEvent = ({ eventId }: { eventId: number }) => {
    if (!eventId) return;

    const modifiedModule = {
      ...module,
      limit: (module.events?.length || 0) + 1,
      events: [...(module.events || []), eventId]
    };

    setTimeout(() => updateModule(modifiedModule));
  };

  const handleMoveEvent = (eventIndex: number, increment: number) => {
    const events = copyEvents();
    const movedEvent = events.splice(eventIndex, 1)[0];
    events.splice(eventIndex + increment, 0, movedEvent);
    updateModule(JSON.parse(JSON.stringify({ ...module, events })));
  };

  const handleRemoveEvent = (eventIndex: number) => {
    const events = copyEvents();
    events.splice(eventIndex, 1);
    updateModule({ ...module, events, limit: events.length });
  };

  return (
    <div>
      {Boolean(url) && loading ? (
        <LoadingIndicator />
      ) : data?.events?.length || includeActions ? (
        <div>
          <div className="events">
            {data?.events?.map((event, i: number) => (
              <div className="event" key={event.id} tabIndex={0}>
                <div className="picture">
                  <CalendarEventTilePicture
                    picture={event.imageUrl}
                    size={480}
                    style={{ height: 60 }}
                  />
                </div>

                <div className="content">
                  <div className="info">
                    <div className="title">{event.title}</div>

                    <div className="event-dates">
                      <CalendarEventDateString
                        startDate={event.startDate as string}
                        endDate={event.endDate as string}
                      />
                    </div>
                  </div>

                  {includeActions ? (
                    <div className="content-actions">
                      <Button
                        category="lighter"
                        disabled={i === 0}
                        icon="arrow-left"
                        isRounded={true}
                        onClick={() => handleMoveEvent(i, -1)}
                        size="small"
                        tooltip={messages.commonMove}
                      />
                      <Button
                        category="lighter"
                        disabled={i === module.events.length - 1}
                        icon="arrow-right"
                        isRounded={true}
                        onClick={() => handleMoveEvent(i, 1)}
                        size="small"
                        tooltip={messages.commonMove}
                      />
                      <Button
                        category="lighter"
                        icon="trash-alt"
                        isRounded={true}
                        onClick={() => handleRemoveEvent(i)}
                        size="small"
                        tooltip={messages.commonRemove}
                      />
                    </div>
                  ) : null}
                </div>
              </div>
            ))}

            {includeActions ? (
              <div className="add">
                <ModalButtonWithForm
                  buttonCategory="transparent"
                  buttonIcon="plus"
                  buttonStyle={{
                    alignItems: 'center',
                    border: `2px dashed ${theme.lightGreyDarkActive}`,
                    borderRadius: theme.cardBorderRadius,
                    display: 'flex',
                    height: '100%',
                    justifyContent: 'center',
                    width: '100%'
                  }}
                  buttonText={messages.commonAddCalendarEvent}
                  modalActionCategory="success"
                  modalActionText={messages.commonAdd}
                  modalTitle={messages.commonAddCalendarEvent}
                  onSubmit={handleAddEvent}
                  preventSubmit={true}
                  pristineIsValid={false}
                >
                  {() => (
                    <Select
                      field="eventId"
                      isSearchable={true}
                      noOptionsMessage={messages.commonCalendarEventNotFound}
                      options={eventOptions.filter(
                        (event) => !(module.events || []).includes(event.value)
                      )}
                      placeholder={messages.calendarEventsSearch}
                    />
                  )}
                </ModalButtonWithForm>
              </div>
            ) : null}
          </div>

          <div className="disclaimer">
            {`* ${messages.homePageModuleCalendarEventsDisclaimer}`}
          </div>
        </div>
      ) : (
        <EmptyIndicator
          icon={['far', 'calendar-days']}
          iconIsDarker={true}
          size="small"
          title={messages.calendarEventsEmpty}
        />
      )}

      <style jsx>{`
        .events {
          display: flex;
          flex-direction: row;
          flex-wrap: wrap;
          gap: 20px;
          padding: 20px;
        }
        .add,
        .event {
          background-color: white;
          border-radius: ${theme.cardBorderRadius};
          outline: 0;
          transition: box-shadow 0.3s;
          width: 100%;
          min-height: 200px;
          min-width: 100px;
        }
        .add :global(> button) {
          background-color: ${theme.lightGreyLighter};
          color: ${theme.textColorLighter};
        }
        .add :global(> button:hover) {
          background-color: white;
          color: ${theme.textColor};
        }
        .picture {
          padding: 10px 10px 0;
        }
        .content {
          color: ${theme.textColor};
          display: flex;
          flex-direction: column;
          flex-grow: 1;
          font-size: 1.5rem;
          font-weight: 600;
          gap: 10px;
          min-height: 130px;
          justify-content: space-between;
          padding: 10px 15px;
        }
        .event-dates {
          color: #555;
          font-size: 1.3rem;
          font-weight: 600;
          margin-top: 5px;
        }
        .title {
          color: ${theme.textColor};
          display: -webkit-box;
          font-size: 1.4rem;
          font-weight: 600;
          overflow: hidden;
          text-overflow: ellipsis;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 3;
        }
        .content-actions {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
          justify-content: flex-end;
        }
        .disclaimer {
          color: ${theme.textColorLight};
          font-size: 1.4rem;
          font-weight: 600;
          margin: 0 20px 20px;
        }
        @media (min-width: ${breakpoints.small}px) {
          .add,
          .event {
            width: calc(50% - 10px);
          }
        }
        @media (min-width: ${breakpoints.medium}px) {
          .add,
          .event {
            width: calc(33.333% - 13.333px);
          }
        }
        @media (min-width: ${breakpoints.large}px) {
          .add,
          .event {
            width: calc(25% - 15px);
          }
        }
      `}</style>
    </div>
  );
};

export default HomePageSettingsTilesCalendarEvents;
